import { BaseClient } from './baseClient';
import { APIResponse } from '../types';
import { BomEditProposal } from '../types/bomProposal';
import { RobbieProposal } from '../types/robbieProposal';
import type { RobbieAction } from '../utils/robbieActivity';

/**
 * `RFQ_UPDATED` / `RECOMMENDATION_UPDATED`: Robbie changed data — refresh.
 * `BOM_EDIT_PROPOSED` / `PROPOSAL_SHOWN`: Robbie changed NOTHING; `proposal`
 * is a card for the buyer to apply, so it must not trigger a refresh.
 * (`RFQ_UPDATED` survives on turns stored before criteria became a proposal.)
 */
export type ChatUiEffect = 'RFQ_UPDATED' | 'RECOMMENDATION_UPDATED' | 'BOM_EDIT_PROPOSED' | 'PROPOSAL_SHOWN';

export interface ChatAction {
    tool: string;
    args?: Record<string, unknown>;
    summary?: string;
    uiEffect?: ChatUiEffect;
    /** Present on proposal actions (live and from history): a BOM diff, or a step card. */
    proposal?: BomEditProposal | RobbieProposal;
}

/**
 * What the buyer is looking at when they message Robbie — pointers only
 * (ids and names of the visible selection), never copies of server data;
 * the backend expands them from its own records. Reported by step components
 * via RobbieUiContext and sent with each chat message.
 */
export interface ChatUiContext {
    /** Which surface is on screen, e.g. 'supplier-discovery', 'supplier-outreach'. */
    view: string;
    componentId?: string;
    componentName?: string;
    supplierName?: string;
    threadId?: string;
    /** One short human-readable line for anything the fields above can't say. */
    detail?: string;
    /** The extracted-BOM lines a request is about (`view: 'extracted-bom'`); none = the whole BOM. */
    componentIds?: string[];
}

/**
 * What a chat message carries: the view a step reports, plus the buyer's
 * recent actions (added at send time from `RobbieUiContext`). A message can
 * carry actions with no view reported, so `view` is optional here.
 */
export type ChatUiPayload = Partial<ChatUiContext> & {
    /** What the buyer just did, oldest first; the server keeps the newest 12. */
    recentActions?: RobbieAction[];
    /** Which step-3 screen is on: 1 context, 2 validation, 3 discovery. */
    subStep?: number;
};

class ChatClient extends BaseClient {
    async getChatHistory(rfqId: string): Promise<any[]> {
        const response = await this.api.get<APIResponse<{ messages: any[] }>>(`/trp/rfqs/${rfqId}/chat`);
        if (response.data.success && response.data.data) {
            return response.data.data.messages;
        }
        return [];
    }

    async sendChatMessage(
        rfqId: string,
        message?: string,
        step?: number,
        tab?: string,
        audio?: { data: string; mimeType: string; durationMs?: number; peaks?: number[] },
        uiContext?: ChatUiPayload
    ): Promise<{ message: any; actions: ChatAction[] }> {
        const response = await this.api.post<APIResponse<{ message: any; actions?: ChatAction[] }>>(`/trp/rfqs/${rfqId}/chat`, {
            message,
            step,
            tab,
            audio,
            uiContext
        });
        if (response.data.success && response.data.data) {
            return {
                message: response.data.data.message,
                actions: response.data.data.actions || []
            };
        }
        throw new Error(response.data.error || 'Failed to send chat message');
    }

    async getChatChips(rfqId: string, step?: number, tab?: string): Promise<string[]> {
        const response = await this.api.get<APIResponse<string[]>>(`/trp/rfqs/${rfqId}/chat/chips`, {
            params: { step, tab }
        });
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return [];
    }
}

export const chatClient = new ChatClient();
